import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useEstateContext } from "@/lib/EstateContext";
import { Building2, ChevronDown, Check, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * EstateSwitcher — always-visible sidebar widget for estate managers.
 * Shows current estate name; if the user manages multiple estates, displays
 * a dropdown to switch between them.
 */
export default function EstateSwitcher() {
  const navigate = useNavigate();
  const { myEstates, activeEstate, activeEstateId, setActiveEstate, isLoadingEstates } = useEstateContext();

  if (isLoadingEstates) {
    return (
      <div className="mx-3 mb-3 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse h-10" />
    );
  }

  if (!activeEstate && myEstates.length === 0) {
    return null;
  }

  const handleSwitch = (estateId) => {
    if (estateId === activeEstateId) return;
    // setActiveEstate already calls setSearchParams to sync the URL,
    // so no separate navigate() is needed (double-navigation causes infinite re-render).
    setActiveEstate(estateId);
  };

  // Single estate — show badge + add button
  if (myEstates.length <= 1) {
    return (
      <div className="mx-3 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900">
        <Building2 className="w-4 h-4 text-green-700 dark:text-green-400 flex-shrink-0" />
        <span className="flex-1 text-sm font-semibold text-green-800 dark:text-green-300 truncate">
          {activeEstate?.name ?? "No estate"}
        </span>
        <button
          onClick={() => navigate("/CreateEstate")}
          title="Add new estate"
          className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-green-200 dark:bg-green-800 hover:bg-green-300 dark:hover:bg-green-700 transition-colors"
        >
          <Plus className="w-3 h-3 text-green-800 dark:text-green-200" />
        </button>
      </div>
    );
  }

  // Multiple estates — show switcher dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="mx-3 mb-3 w-[calc(100%-1.5rem)] flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors text-left">
          <Building2 className="w-4 h-4 text-green-700 dark:text-green-400 flex-shrink-0" />
          <span className="flex-1 text-sm font-semibold text-green-800 dark:text-green-300 truncate">
            {activeEstate?.name ?? "Select estate"}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-green-600 dark:text-green-500 flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        sideOffset={6}
        className="w-56 rounded-2xl border-teal-100 dark:border-slate-800"
      >
        {myEstates.map(estate => (
          <DropdownMenuItem
            key={estate.id}
            onClick={() => handleSwitch(estate.id)}
            className="rounded-xl cursor-pointer flex items-center gap-2"
          >
            <Check
              className={`w-4 h-4 flex-shrink-0 ${estate.id === activeEstateId ? "text-green-600" : "invisible"}`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{estate.name}</p>
              {estate.location && (
                <p className="text-xs text-slate-500 truncate">{estate.location}</p>
              )}
            </div>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-teal-100 dark:bg-slate-800" />
        <DropdownMenuItem
          onClick={() => navigate(createPageUrl("CreateEstate"))}
          className="rounded-xl cursor-pointer text-green-700 dark:text-green-400 font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add new estate
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
